import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import React from "react";

import {
  loadImageDimensions,
  urlToBase64,
  capitalizeFirstLetter,
} from "../modules/invoiceUtils";
import { usePermission } from "../../context/PermissionContext";
import uRLs from "../../utils/baseUtils";

export const generateIN_HA4PRM_5PDF = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
  const { documentConfig, isPreSale,  } = invoiceConfig;
  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const HALF_PAGE_HEIGHT = 140;
  const PADDING = 5;
  const MARGIN_LEFT = PADDING;
  const MARGIN_RIGHT = pageW - PADDING;
  const centerX = pageW / 2;
  let y = PADDING; // Starting height
  try {
    let finalH = 0;
    let finalW = 0;
    let logoBase64 = null;

    // 1. SAFELY ATTEMPT TO LOAD & DRAW LOGO (if provided)
    if (documentConfig?.image) {
      try {
        const imagePath = documentConfig.image;
        logoBase64 = await urlToBase64(imagePath);
        if (logoBase64) {
          const { width, height } = await loadImageDimensions(logoBase64);
          const maxWidth = 30;
          const maxHeight = 12;
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          finalW = width * ratio;
          finalH = height * ratio;

          doc.addImage(logoBase64, "PNG", MARGIN_LEFT, y, finalW, finalH);
        }
      } catch (imgErr) {
        console.warn("Logo image failed to load/render:", imgErr);
        finalW = 0;
        finalH = 0;
      }
    }

    const headerTopY = y;

    // ==================== LEFT SIDE ====================
    // If logo exists, shift text to the right of logo. Otherwise, align with MARGIN_LEFT
    const leftStartX = finalW > 0 ? MARGIN_LEFT + finalW + 4 : MARGIN_LEFT;

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(0);

    doc.text(
      documentConfig?.fromData?.title?.toUpperCase() || "",
      leftStartX,
      headerTopY + 3,
    );

    let leftY = headerTopY + 6;

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(6.8);
    doc.setTextColor(0);

    const addrLine1 = [
      documentConfig?.fromData?.addressline1,
      documentConfig?.fromData?.addressline2,
      documentConfig?.fromData?.district,
      documentConfig?.fromData?.state,
      documentConfig?.fromData?.pincode,
    ]
      .filter(Boolean)
      .join(", ");

    if (addrLine1) {
      doc.text(addrLine1, leftStartX, leftY);
      leftY += 3;
    }

    const phoneStr = `${
      documentConfig?.fromData?.phone ? documentConfig.fromData.phone : ""
    }${
      documentConfig?.fromData?.additionalPhone
        ? `, ${documentConfig.fromData.additionalPhone}`
        : ""
    }`.trim();

    const gstin = documentConfig?.fromData?.gstnumber;

    if (phoneStr) {
      doc.setFont("Inter-Bold", "bold");
      doc.text("Ph:", leftStartX, leftY);

      doc.setFont("Inter-Regular", "normal");
      doc.text(phoneStr, leftStartX + doc.getTextWidth("Ph: ") + 1, leftY);

      leftY += 3;
    }

    if (gstin) {
      doc.setFont("Inter-Bold", "bold");
      doc.text("GSTIN:", leftStartX, leftY);

      doc.setFont("Inter-Regular", "normal");
      doc.text(gstin, leftStartX + doc.getTextWidth("GSTIN: ") + 1, leftY);

      leftY += 3;
    }

    // ==================== RIGHT SIDE ====================
    const invLabel = documentConfig?.numberLabel || "";
    const invVal = documentConfig?.number || "-";
    const invDateTime = documentConfig?.Date?.time;

    const dateStr = `DATE: ${documentConfig?.Date?.date || ""}`;
    const timeStr = `TIME: ${invDateTime || "-"}`;
    const combinedDateTimeStr = `${dateStr}   ${timeStr}`;
    let rightY = headerTopY + 3;

    // 1. TAX INVOICE Heading
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.text(documentConfig?.title || "", MARGIN_RIGHT, rightY, {
      align: "right",
    });

    rightY += 4;

    // 2. Invoice No. Block
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(0);

    const invValW = doc.getTextWidth(invVal) + 2;
    doc.text(invLabel, MARGIN_RIGHT - invValW, rightY, { align: "right" });

    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    doc.text(invVal, MARGIN_RIGHT, rightY, { align: "right" });

    rightY += 2;

    // 3. Separator Line
    doc.setFont("Inter-Regular", "normal");
    doc.setTextColor(0);
    doc.setFontSize(7);
    const totalDateTimeWidth = doc.getTextWidth(combinedDateTimeStr);
    const lineStartX = MARGIN_RIGHT - totalDateTimeWidth;

    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor || 200);
    doc.setLineWidth(0.1);
    doc.line(lineStartX, rightY, MARGIN_RIGHT, rightY);

    rightY += 3;

    // 4. Date and Time
    doc.setTextColor(0);
    doc.setFont("Inter-Regular", "normal");
    doc.text(combinedDateTimeStr, MARGIN_RIGHT, rightY, { align: "right" });

    // ==================== HEADER BOTTOM ====================
    const headerBottom = Math.max(leftY, rightY, headerTopY + finalH);

    y = headerBottom + 1;
  } catch (e) {
    console.warn("Header layout failed", e);
    y += 15;
  }
  y += 1;

  if (documentConfig?.toData) {
    let rowHeight = 5;
    doc.setDrawColor(
      selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
    );
    doc.setLineWidth(0.2);

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8);
    doc.setTextColor(0);
    doc.text("To : ", MARGIN_LEFT + 2, y + 3.5);

    const labelWidth = doc.getTextWidth("To : ");
    const nameXPosition = MARGIN_LEFT + 2 + labelWidth;

    doc.setFont("Inter-Bold", "bold");
    doc.text(documentConfig?.toData?.name || "", nameXPosition, y + 3.5);
    doc.setFont("Inter-Regular", "normal");
    rowHeight += 3;

    const fullAddress = [
      [
        documentConfig?.toData?.addressline1,
        documentConfig?.toData?.addressline2,
        documentConfig?.toData?.district,
        documentConfig?.toData?.state,
        documentConfig?.toData?.pincode
          ? `- ${documentConfig?.toData?.pincode}`
          : "",
      ],
      [
        data.saleto?.gstnumber ? `GSTIN: ${data.saleto.gstnumber}` : "",
        data.saleto?.phone ? `Mobile: ${data.saleto.phone}` : "",
        data.saleto?.referenceid
          ? `Reference No: ${data.saleto.referenceid}`
          : "",
        data.saleto?.fssai ? `FASSI: ${data.saleto.fssai}` : "",
        data.saleto?.cinnumber ? `CIN: ${data.saleto.cinnumber}` : "",
        data.saleto?.pannumber ? `PAN: ${data.saleto.pannumber}` : "",
      ]
        .filter(Boolean)
        .join(" | "),
    ]
      .filter(Boolean)
      .join("\n");

    if (fullAddress) {
      const wrappedAddr = doc.splitTextToSize(fullAddress, pageW / 2 + 10);
      doc.text(wrappedAddr, MARGIN_LEFT + 2, y + 8);
      rowHeight += wrappedAddr.length * 3;
    }

    doc.rect(MARGIN_LEFT, y, MARGIN_RIGHT - MARGIN_LEFT, rowHeight);
    y += rowHeight;
  }

  /* -------------------------------------------------
     PRODUCT TABLE
  ------------------------------------------------- */
  const rowCount = data?.saleitems?.length;
  const standardHeight = 4;
  const targetTotalHeight = 40;
  const extraSpace =
    rowCount > 0 && rowCount <= 5
      ? targetTotalHeight - rowCount * standardHeight
      : 0;
  autoTable(doc, {
    startY: y,
    theme: "grid",
    rowPageBreak: "avoid",
      margin: {
      top: PADDING,
      left: PADDING,
      right: PADDING,
      bottom: pageH - HALF_PAGE_HEIGHT + PADDING,
    },
    styles: {
      font: "Inter-Regular",
      fontSize: 7,
      cellPadding: 1,
      valign: "middle",
      overflow: "linebreak",
      lineWidth: 0.2,
      textColor: "#000000",
    },
    headStyles: {
      font: "Inter-Bold",
      fontSize: 7,
      halign: "center",
      valign: "middle",
      cellPadding: 1,
      fillColor: selectedTheme?.templateTheme?.primaryColor,
      textColor: selectedTheme?.templateTheme?.foregroundColor,
    },
    head: [
      [
        "S.No",
        "Description",
        "HSN/SAC",
        "MRP",
        "Base Price",
        "Qty",
        "Tax Val",
        "Tax",
        "Tax Amt",
        "Amount",
      ],
    ],
    body: documentConfig?.items.map((it, i) => {
      const rate = it.lineTotalTaxAmount;
      const amount = it.lineGrandTotal;
      const mrp = it.mrp;
      const baseprice = it.baseprice;
      const landingCost = it?.lineTotalLandingCost;
      const taxableValue = it.lineTotalBasePrice;

      return [
        i + 1,
        [it.title, it.note ? `-(${it.note})` : ""].filter(Boolean),
        it.hsnsac || "",
        `${mrp}`,
        `${baseprice}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${taxableValue}`,
        `${it.taxpercentage || 0}%`,
        `${rate}`,
        `${amount}`,
      ];
    }),
    columnStyles: {
      0: { cellWidth: 10, halign: "center" },
      1: { cellWidth: "auto", halign: "left" },
      2: { cellWidth: 16, halign: "center" },
      3: { cellWidth: 21, halign: "right" },
      4: { cellWidth: 21, halign: "right" },
      5: { cellWidth: 12, halign: "center" },
      6: { cellWidth: 21, halign: "right" },
      7: { cellWidth: 12, halign: "center" },
      8: { cellWidth: 21, halign: "right" },
      9: { cellWidth: 21, halign: "right" },
    },
    didParseCell: function (data) {
      if (data.section === "head") {
        const colId = data.column.index;
          if ([0,1,2, 3,4,5,6,7,8,9].includes(colId)) {
          data.cell.styles.halign = "center";
        }
      }     
    },
  });

  const tableEndY = doc.lastAutoTable.finalY;
  const borderColor = selectedTheme?.templateTheme?.borderColor || [
    200, 200, 200,
  ];

  doc.setDrawColor(borderColor);
  doc.setLineWidth(0.1);

  const summaryRowY = tableEndY;
  // const summaryRowH = 5;

  // doc.rect(PADDING, summaryRowY, pageW - PADDING * 2, summaryRowH);

  // const colWidths = [10, 36, 15, 16, 16, 16, 16, 16, 11, 16, 16, 16];
  // let x = PADDING;

  // colWidths.forEach((w) => {
  //   x += w;
  //   doc.line(x, summaryRowY, x, summaryRowY + summaryRowH);
  // });

  // const summaryCenterY = summaryRowY + 3.5;
  // doc.setTextColor(0);
  // doc.setFont("Inter-Bold", "bold");
  // doc.setFontSize(6.3);
  // doc.text("Total :", PADDING + 36, summaryCenterY);
  // doc.text(`${documentConfig?.totalMrp || 0}`, PADDING + 75, summaryCenterY, {
  //   align: "right",
  // });
  // doc.text(
  //   `${documentConfig?.totalDiscount || 0}`,
  //   PADDING + 92,
  //   summaryCenterY,
  //   { align: "right" },
  // );
  // doc.text(
  //   `${documentConfig?.totalSalePrice || 0}`,
  //   PADDING + 108,
  //   summaryCenterY,
  //   { align: "right" },
  // );

  // doc.text(
  //   `${documentConfig?.totalTaxAmount || 0}`,
  //   PADDING + 135,
  //   summaryCenterY,
  //   { align: "right" },
  // );
  // doc.text(
  //   `${documentConfig?.cgstAmount || 0}`,
  //   PADDING + 167,
  //   summaryCenterY,
  //   { align: "right" },
  // );
  // doc.text(
  //   `${documentConfig?.sgstAmount || 0}`,
  //   PADDING + 183,
  //   summaryCenterY,
  //   { align: "right" },
  // );
  // doc.text(
  //   `${documentConfig?.grandTotal || 0}`,
  //   pageW - PADDING - 2,
  //   summaryCenterY,
  //   { align: "right" },
  // );

  const rowH = 5;
  const wordsRowY = summaryRowY ;
  const rightSectionWidth = 54;
  const totalLabelX = MARGIN_RIGHT - rightSectionWidth;
  doc.setTextColor(0);
  doc.rect(PADDING, wordsRowY, pageW - PADDING * 2, rowH);
  doc.line(totalLabelX, wordsRowY, totalLabelX, wordsRowY + rowH);

  const textY = wordsRowY + rowH / 2 + 1;

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(6.5);
  doc.text("Rupees in words:", PADDING + 2, textY);

  doc.setFont("Inter-Regular", "normal");
  const words = documentConfig?.amountInWords || "Zero Rupees Only";
  doc.text(words, PADDING + 23, textY, {
    maxWidth: totalLabelX - PADDING - 17,
  });

  doc.setFont("Inter-Bold", "bold");
  doc.text(
    `TOTAL   AMOUNT : ${documentConfig?.grandTotal}`,
    MARGIN_RIGHT - 2,
    textY,
    { align: "right" },
  );

  // =====================================================
  // TERMS & CONDITIONS SECTION (With Dynamic Page Break)
  // =====================================================
  // let currentSectionY = wordsRowY + rowH + 7;
  //=====================================================
  // REMARK SECTION (Below Amount in Words)
  //=====================================================

  let currentSectionY = wordsRowY + rowH + 4;

  if (documentConfig?.remarks?.trim()) {
    const remarkTitle = "Remark";
    const remarkWidth = pageW - PADDING * 2;

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);

    const remarkLines = doc.splitTextToSize(
      capitalizeFirstLetter(documentConfig.remarks),
      remarkWidth - 6,
    );

    const lineHeight = 3.5;
    const paddingTop = 2;
    const paddingBottom = 2;

    const remarkHeight =
      remarkLines.length * lineHeight + paddingTop + paddingBottom;
    // Move to next page if remark doesn't fit
    if (currentSectionY + remarkHeight + 25 > HALF_PAGE_HEIGHT - 5) {
      doc.addPage();
      currentSectionY = PADDING + 5;
    }

    // Heading
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.text(remarkTitle, PADDING, currentSectionY);

    currentSectionY += 2;

    // Border
    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor || 180);
    doc.setLineWidth(0.2);

    // Text
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);

    let remarkY = currentSectionY + 3;

    remarkLines.forEach((line) => {
      doc.text(line, PADDING, remarkY);
      remarkY += lineHeight;
    });

    currentSectionY += remarkHeight + 2;
  }

  // ==============================
  // Bank + QR + Signature Section
  // ==============================

  let sectionY = currentSectionY;

  const leftX = PADDING;
  const qrSize = 28;
  const qrX = (pageW - qrSize) / 2;
  const rightX = MARGIN_RIGHT;

  // ---------- Calculate Heights ----------
  let bankHeight = 0;

  if (
   documentConfig?.bankDetails 
  ) {
    bankHeight = 5; // title

    if (documentConfig?.bankDetails?.accountNo) bankHeight += 4;

    if (documentConfig?.bankDetails?.ifsc) bankHeight += 4;
    if (documentConfig?.bankDetails?.beneficiaryName) bankHeight += 4;
    if (documentConfig?.bankDetails?.bankName) bankHeight += 4;
    if (documentConfig?.bankDetails?.branch) bankHeight += 4;
    if (documentConfig?.upiId) bankHeight += 4;
  }

  let qrHeight = qrCodeDataUrl  ? qrSize : 0;

  let signHeight = storeSettings?.invoiceSettings?.signature ? 22 : 14;

  // Take tallest block
  const blockHeight = Math.max(bankHeight, qrHeight, signHeight);

  // Page break if required
  if (sectionY + blockHeight > pageH - PADDING) {
    doc.addPage();
    sectionY = PADDING;
  }
  let bankY = sectionY + 2;

  if (   
    documentConfig?.bankDetails 
  ) {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text("Bank Details", leftX, bankY);
    bankY += 5;
    const bankDetails = [];

    if (documentConfig?.bankDetails?.accountNo) {
      bankDetails.push({
        label: "Account #",
        value: documentConfig?.bankDetails.accountNo,
      });
    }

    if (documentConfig?.bankDetails?.ifsc) {
      bankDetails.push({
        label: "IFSC",
        value: documentConfig?.bankDetails.ifsc,
      });
    }
    if (documentConfig?.bankDetails?.beneficiaryName) {
      bankDetails.push({
        label: "Name",
        value: documentConfig?.bankDetails.beneficiaryName,
      });
    }
    if (documentConfig?.bankDetails?.bankName) {
      bankDetails.push({
        label: "Name",
        value: documentConfig?.bankDetails.bankName,
      });
    }
    if (documentConfig?.bankDetails?.branch) {
      bankDetails.push({
        label: "Name",
        value: documentConfig?.bankDetails.branch,
      });
    }
    if (storeSettings?.paymentSettings?.upiId) {
      bankDetails.push({ label: "UPI", value: documentConfig?.upiId });
    }

    const labelWidthOffset = 18;

    bankDetails.forEach((item) => {
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.text(`${item.label} :`, leftX, bankY);
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.text(String(item.value || ""), leftX + labelWidthOffset, bankY);

      bankY += 4.5;
    });

    doc.setTextColor(0, 0, 0);
  }

  if (qrCodeDataUrl) {
    doc.addImage(qrCodeDataUrl, "PNG", qrX, sectionY - 3, qrSize, qrSize);
  }

  let signBottom = sectionY;

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  sectionY += 2;
  doc.text(`For ${data?.salefrom?.title}`, rightX, sectionY, {
    align: "right",
  });

  if (storeSettings?.invoiceSettings?.signature) {
    try {
      const signimg = uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;

      const base64 = await urlToBase64(signimg);

      const { width, height } = await loadImageDimensions(base64);

      const maxWidth = 25;
      const maxHeight = 10;

      const ratio = Math.min(maxWidth / width, maxHeight / height);

      const imgW = width * ratio;
      const imgH = height * ratio;

      doc.addImage(
        base64,
        signimg.endsWith(".png") ? "PNG" : "JPEG",
        rightX - imgW,
        sectionY + 4,
        imgW,
        imgH,
      );

      signBottom = sectionY + 4 + imgH;
    } catch {}
  } else {
    doc.line(rightX - 30, sectionY + 5, rightX, sectionY + 5);

    signBottom = sectionY + 5;
  }

  doc.setFont("Inter-Regular", "normal");
  doc.text("Authorized Signatory", rightX, signBottom + 3, { align: "right" });

  signBottom += 5;

  let sectionEndY = Math.max(bankY, sectionY + qrHeight, signBottom);

  doc.setFont("Inter-Regular", "normal");
  doc.setFontSize(7);
  doc.setTextColor(0);

  // =====================================================
  y = currentSectionY +  4;

  if (storeSettings?.negobillBranding) {
    const minGapY = y + 3;
    const bottomLimitY = HALF_PAGE_HEIGHT - 5;
    let finalBrandingY = Math.max(minGapY, bottomLimitY);

    // Safety check if branding calculations push out of the active breakdown sheet
    if (finalBrandingY > doc.internal.pageSize.getHeight() - 10) {
      finalBrandingY = doc.internal.pageSize.getHeight() - 10;
    }

    const normalText = "Powered by ";
    const boldText = "NegoBill.com";

    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const normalW = doc.getTextWidth(normalText);
    doc.setFont("Inter-Bold", "bold");
    const boldW = doc.getTextWidth(boldText);
    const totalWidth = normalW + boldW;
    const startX = MARGIN_RIGHT - totalWidth;

    doc.setFont("Inter-Regular", "normal");
    doc.text(normalText, startX, finalBrandingY);
    doc.setFont("Inter-Bold", "bold");
    doc.text(boldText, startX + normalW, finalBrandingY);
  }

  return doc;
};

export const generateBILL_HA4PRM_5PDF = async (
  data,
  qrCodeDataUrl,
  terms,
  invoiceConfig,
  selectedTheme,  
  storeSettings,
) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const HALF_PAGE_HEIGHT = 140;
  const PADDING = 5;
  const MARGIN_LEFT = PADDING;
  const MARGIN_RIGHT = pageW - PADDING;
  const centerX = pageW / 2;
  let y = PADDING; // Starting height
  const { documentConfig, isPreSale, timezone } = invoiceConfig;
  try {
    let finalH = 0;
    let finalW = 0;
    let logoBase64 = null;

    // 1. SAFELY ATTEMPT TO LOAD & DRAW LOGO (if provided)
    if (documentConfig?.image) {
      try {
        const imagePath = documentConfig.image;
        logoBase64 = await urlToBase64(imagePath);
        if (logoBase64) {
          const { width, height } = await loadImageDimensions(logoBase64);
          const maxWidth = 30;
          const maxHeight = 12;
          const ratio = Math.min(maxWidth / width, maxHeight / height);
          finalW = width * ratio;
          finalH = height * ratio;

          doc.addImage(logoBase64, "PNG", MARGIN_LEFT, y, finalW, finalH);
        }
      } catch (imgErr) {
        console.warn("Logo image failed to load/render:", imgErr);
        finalW = 0;
        finalH = 0;
      }
    }

    const headerTopY = y;

    // ==================== LEFT SIDE ====================
    // If logo exists, shift text to the right of logo. Otherwise, align with MARGIN_LEFT
    const leftStartX = finalW > 0 ? MARGIN_LEFT + finalW + 4 : MARGIN_LEFT;

    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(7.5);
    doc.setTextColor(0);

    doc.text(
      documentConfig?.fromData?.title?.toUpperCase() || "",
      leftStartX,
      headerTopY + 3,
    );

    let leftY = headerTopY + 6;

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(6.8);
    doc.setTextColor(0);

    const addrLine1 = [
      documentConfig?.fromData?.addressline1,
      documentConfig?.fromData?.addressline2,
      documentConfig?.fromData?.district,
      documentConfig?.fromData?.state,
      documentConfig?.fromData?.pincode,
    ]
      .filter(Boolean)
      .join(", ");

    if (addrLine1) {
      doc.text(addrLine1, leftStartX, leftY);
      leftY += 3;
    }

    const phoneStr = `${
      documentConfig?.fromData?.phone ? documentConfig.fromData.phone : ""
    }${
      documentConfig?.fromData?.additionalPhone
        ? `, ${documentConfig.fromData.additionalPhone}`
        : ""
    }`.trim();

    const gstin = documentConfig?.fromData?.gstnumber;

    if (phoneStr) {
      doc.setFont("Inter-Bold", "bold");
      doc.text("Ph:", leftStartX, leftY);

      doc.setFont("Inter-Regular", "normal");
      doc.text(phoneStr, leftStartX + doc.getTextWidth("Ph: ") + 1, leftY);

      leftY += 3;
    }

    if (gstin) {
      doc.setFont("Inter-Bold", "bold");
      doc.text("GSTIN:", leftStartX, leftY);

      doc.setFont("Inter-Regular", "normal");
      doc.text(gstin, leftStartX + doc.getTextWidth("GSTIN: ") + 1, leftY);

      leftY += 3;
    }

    // ==================== RIGHT SIDE ====================
    const invLabel = documentConfig?.numberLabel || "";
    const invVal = documentConfig?.number || "-";
    const invDateTime = documentConfig?.Date?.time;

    const dateStr = `DATE: ${documentConfig?.Date?.date || ""}`;
    const timeStr = `TIME: ${invDateTime || "-"}`;
    const combinedDateTimeStr = `${dateStr}   ${timeStr}`;
    let rightY = headerTopY + 3;

    // 1. TAX INVOICE Heading
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(9);
    doc.setTextColor(0);
    doc.text(documentConfig?.title || "", MARGIN_RIGHT, rightY, {
      align: "right",
    });

    rightY += 4;

    // 2. Invoice No. Block
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(0);

    const invValW = doc.getTextWidth(invVal) + 2;
    doc.text(invLabel, MARGIN_RIGHT - invValW, rightY, { align: "right" });

    doc.setFont("Inter-Bold", "bold");
    doc.setTextColor(0);
    doc.text(invVal, MARGIN_RIGHT, rightY, { align: "right" });

    rightY += 2;

    // 3. Separator Line
    doc.setFont("Inter-Regular", "normal");
    doc.setTextColor(0);
    doc.setFontSize(7);
    const totalDateTimeWidth = doc.getTextWidth(combinedDateTimeStr);
    const lineStartX = MARGIN_RIGHT - totalDateTimeWidth;

    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor || 200);
    doc.setLineWidth(0.1);
    doc.line(lineStartX, rightY, MARGIN_RIGHT, rightY);

    rightY += 3;

    // 4. Date and Time
    doc.setTextColor(0);
    doc.setFont("Inter-Regular", "normal");
    doc.text(combinedDateTimeStr, MARGIN_RIGHT, rightY, { align: "right" });

    // ==================== HEADER BOTTOM ====================
    const headerBottom = Math.max(leftY, rightY, headerTopY + finalH);

    y = headerBottom + 1;
  } catch (e) {
    console.warn("Header layout failed", e);
    y += 15;
  }
  y += 1;

  if (documentConfig?.toData) {
    let rowHeight = 5;
    doc.setDrawColor(
      selectedTheme?.templateTheme?.borderColor || [200, 200, 200],
    );
    doc.setLineWidth(0.2);

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(8);
    doc.setTextColor(0);
    doc.text("To : ", MARGIN_LEFT + 2, y + 3.5);

    const labelWidth = doc.getTextWidth("To : ");
    const nameXPosition = MARGIN_LEFT + 2 + labelWidth;

    doc.setFont("Inter-Bold", "bold");
    doc.text(documentConfig?.toData?.name || "", nameXPosition, y + 3.5);
    doc.setFont("Inter-Regular", "normal");
    rowHeight += 3;

    const fullAddress = [
      [
        documentConfig?.toData?.addressline1,
        documentConfig?.toData?.addressline2,
        documentConfig?.toData?.district,
        documentConfig?.toData?.state,
        documentConfig?.toData?.pincode
          ? `- ${documentConfig?.toData.pincode}`
          : "",
      ],
      [
        documentConfig?.toData?.gstnumber
          ? `GSTIN: ${documentConfig?.toData.gstnumber}`
          : "",
        documentConfig?.toData?.phone
          ? `Mobile: ${documentConfig?.toData.phone}`
          : "",
        documentConfig?.toData?.referenceid
          ? `Reference No: ${documentConfig?.toData.referenceid}`
          : "",
        documentConfig?.toData?.fssai
          ? `FASSI: ${documentConfig?.toData.fssai}`
          : "",
        documentConfig?.toData?.cinnumber
          ? `CIN: ${documentConfig?.toData.cinnumber}`
          : "",
        documentConfig?.toData?.pannumber
          ? `PAN: ${documentConfig?.toData.pannumber}`
          : "",
      ]
        .filter(Boolean)
        .join(" | "),
    ]
      .filter(Boolean)
      .join("\n ");

    if (fullAddress) {
      const wrappedAddr = doc.splitTextToSize(fullAddress, pageW / 2 + 10);
      doc.text(wrappedAddr, MARGIN_LEFT + 2, y + 8);
      rowHeight += wrappedAddr.length * 3;
    }

    doc.rect(MARGIN_LEFT, y, MARGIN_RIGHT - MARGIN_LEFT, rowHeight);
    y += rowHeight;
  }

  /* -------------------------------------------------
     PRODUCT TABLE
  ------------------------------------------------- */
  const rowCount = data?.saleitems?.length;
  const standardHeight = 4;
  const targetTotalHeight = 40;
  const extraSpace =
    rowCount > 0 && rowCount <= 5
      ? targetTotalHeight - rowCount * standardHeight
      : 0;

  autoTable(doc, {
    startY: y,
    theme: "grid",
    rowPageBreak: "avoid",
      margin: {
      top: PADDING,
      left: PADDING,
      right: PADDING,
      bottom: pageH - HALF_PAGE_HEIGHT + PADDING,
    },
    styles: {
      font: "Inter-Regular",
      fontSize: 7,
      cellPadding: 1,
      valign: "middle",
      overflow: "linebreak",
      lineWidth: 0.2,
      textColor: "#000000",
    },
    headStyles: {
      font: "Inter-Bold",
      fontSize: 7,
      halign: "center",
      valign: "middle",
      cellPadding: 1,
      fillColor: selectedTheme?.templateTheme?.primaryColor,
      textColor: selectedTheme?.templateTheme?.foregroundColor,
    },
    bodyStyles: {
      minCellHeight: Math.max(2, standardHeight),
    },
    head: [
      [
        "S.No",
        "Description",
        "HSN/SAC",
        "MRP",
        "Discount",
        "Rate",
        "Qty",
        "Amount",
      ],
    ],
  body: documentConfig?.items?.map((it, i) => {
      const amount = it.lineGrandTotal;
      const mrp = it.mrp;
      const Discount = it.discount;
      const rate = it.soldprice;
      const landingCost = it.lineTotalLandingCost;

      return [
        i + 1,
        [it.title, it.note ? `-(${it.note})` : ""].filter(Boolean),
        it.hsnsac || "",
        `${mrp}`,
        `${Discount}`,
        `${rate}`,
        `${it.quantity} ${(() => {
          const unitCode = it?.isSecondaryUnit
            ? it?.unitConfig?.secondaryUnitCode
            : it?.unitConfig?.primaryUnitCode;
          return unitCode || "";
        })()}`,
        `${amount}`,
      ];
    }),
     columnStyles: {
      0: { cellWidth: 12, halign: "center" },
      1: { cellWidth: "auto", halign: "left" },
      2: { cellWidth: 16, halign: "center" },
      3: { cellWidth: 21, halign: "right" },
      4: { cellWidth: 21, halign: "right" },
      5: { cellWidth: 21, halign: "right" },
      6: { cellWidth: 12, halign: "center" },
      7: { cellWidth: 21, halign: "right" },      
    },
    didParseCell: function (data) {
      if (data.section === "head") {
        const colId = data.column.index;
          if ([0,1,2, 3,4,5,6,7,8].includes(colId)) {
          data.cell.styles.halign = "center";
        }
      }     
    },
 
  });

  const tableEndY = doc.lastAutoTable.finalY;
  const borderColor = selectedTheme?.templateTheme?.borderColor || [
    200, 200, 200,
  ];

  doc.setDrawColor(borderColor);
  doc.setLineWidth(0.1);

  const summaryRowY = tableEndY;

  const rowH = 5;
  const wordsRowY = summaryRowY ;
  const rightSectionWidth = 54;
  const totalLabelX = MARGIN_RIGHT - rightSectionWidth;
  doc.setTextColor(0);
  doc.rect(PADDING, wordsRowY, pageW - PADDING * 2, rowH);
  doc.line(totalLabelX, wordsRowY, totalLabelX, wordsRowY + rowH);

  const textY = wordsRowY + rowH / 2 + 1;

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(6.5);
  doc.text("Rupees in words:", PADDING + 2, textY);

  doc.setFont("Inter-Regular", "normal");
  const words = documentConfig?.amountInWords || "Zero Rupees Only";
  doc.text(words, PADDING + 23, textY, {
    maxWidth: totalLabelX - PADDING - 17,
  });

  doc.setFont("Inter-Bold", "bold");
  doc.text(
    `TOTAL   AMOUNT : ${documentConfig?.grandTotal}`,
    MARGIN_RIGHT - 2,
    textY,
    { align: "right" },
  );

  // =====================================================
  // TERMS & CONDITIONS SECTION (With Dynamic Page Break)
  // =====================================================
  let currentSectionY = wordsRowY + rowH + 4;

  if (documentConfig?.remarks?.trim()) {
    const remarkTitle = "Remark";
    const remarkWidth = pageW - PADDING * 2;

    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);

    const remarkLines = doc.splitTextToSize(
      capitalizeFirstLetter(documentConfig?.remarks),
      remarkWidth - 6,
    );

    const lineHeight = 3.5;
    const paddingTop = 2;
    const paddingBottom = 2;

    const remarkHeight =
      remarkLines.length * lineHeight + paddingTop + paddingBottom;
    // Move to next page if remark doesn't fit
    if (currentSectionY + remarkHeight + 25 > HALF_PAGE_HEIGHT - 5) {
      doc.addPage();
      currentSectionY = PADDING + 5;
    }

    // Heading
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.text(remarkTitle, PADDING, currentSectionY);

    currentSectionY += 2;

    // Border
    doc.setDrawColor(selectedTheme?.templateTheme?.borderColor || 180);
    doc.setLineWidth(0.2);

    // Text
    doc.setFont("Inter-Regular", "normal");
    doc.setFontSize(7);

    let remarkY = currentSectionY + 3;

    remarkLines.forEach((line) => {
      doc.text(line, PADDING, remarkY);
      remarkY += lineHeight;
    });

    currentSectionY += remarkHeight + 2;
  }

  // ==============================
  // Bank + QR + Signature Section
  // ==============================

  let sectionY = currentSectionY;

  const leftX = PADDING;
  const qrSize = 28;
  const qrX = (pageW - qrSize) / 2;
  const rightX = MARGIN_RIGHT;

  // ---------- Calculate Heights ----------
  let bankHeight = 0;

  if (
    documentConfig?.bankDetails
  ) {
    bankHeight = 5; // title

    if (documentConfig?.bankDetails?.accountNo) bankHeight += 4;

    if (documentConfig?.bankDetails?.ifsc) bankHeight += 4;
    if (documentConfig?.bankDetails?.beneficiaryName) bankHeight += 4;
    if (documentConfig?.bankDetails?.bankName) bankHeight += 4;
    if (documentConfig?.bankDetails?.branch) bankHeight += 4;
    if (documentConfig?.upiId) bankHeight += 4;
  }

  let qrHeight = qrCodeDataUrl  ? qrSize : 0;

  let signHeight = storeSettings?.invoiceSettings?.signature ? 22 : 14;

  // Take tallest block
  const blockHeight = Math.max(bankHeight, qrHeight, signHeight);

  // Page break if required
  if (sectionY + blockHeight > pageH - PADDING) {
    doc.addPage();
    sectionY = PADDING;
  }
  let bankY = sectionY + 2;

  if (
    documentConfig?.bankDetails 
  ) {
    doc.setFont("Inter-Bold", "bold");
    doc.setFontSize(8);
    doc.setTextColor(0, 0, 0);
    doc.text("Bank Details", leftX, bankY);
    bankY += 5;
    const bankDetails = [];
    if (documentConfig?.bankDetails?.accountNo) {
      bankDetails.push({
        label: "Account #",
        value: documentConfig?.bankDetails.accountNo,
      });
    }

    if (documentConfig?.bankDetails?.ifsc) {
      bankDetails.push({
        label: "IFSC",
        value: documentConfig?.bankDetails.ifsc,
      });
    }
    if (documentConfig?.bankDetails?.beneficiaryName) {
      bankDetails.push({
        label: "Name",
        value: documentConfig?.bankDetails.beneficiaryName,
      });
    }
    if (documentConfig?.bankDetails?.bankName) {
      bankDetails.push({
        label: "Name",
        value: documentConfig?.bankDetails.bankName,
      });
    }
    if (documentConfig?.bankDetails?.branch) {
      bankDetails.push({
        label: "Name",
        value: documentConfig?.bankDetails.branch,
      });
    }
    if (storeSettings?.paymentSettings?.upiId) {
      bankDetails.push({ label: "UPI", value: documentConfig?.upiId });
    }

    const labelWidthOffset = 18;

    bankDetails.forEach((item) => {
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.text(`${item.label} :`, leftX, bankY);
      doc.setFont("Inter-Regular", "normal");
      doc.setFontSize(7);
      doc.setTextColor(0, 0, 0);
      doc.text(String(item.value || ""), leftX + labelWidthOffset, bankY);

      bankY += 4.5;
    });

    doc.setTextColor(0, 0, 0);
  }

  if (qrCodeDataUrl) {
    doc.addImage(qrCodeDataUrl, "PNG", qrX, sectionY - 3, qrSize, qrSize);
  }

  let signBottom = sectionY;

  doc.setFont("Inter-Bold", "bold");
  doc.setFontSize(8);
  sectionY += 2;
  doc.text(`For ${data?.salefrom?.title}`, rightX, sectionY, {
    align: "right",
  });

  if (storeSettings?.invoiceSettings?.signature) {
    try {
      const signimg = uRLs.IMAGE_URL + storeSettings.invoiceSettings.signature;

      const base64 = await urlToBase64(signimg);

      const { width, height } = await loadImageDimensions(base64);

      const maxWidth = 25;
      const maxHeight = 10;

      const ratio = Math.min(maxWidth / width, maxHeight / height);

      const imgW = width * ratio;
      const imgH = height * ratio;

      doc.addImage(
        base64,
        signimg.endsWith(".png") ? "PNG" : "JPEG",
        rightX - imgW,
        sectionY + 4,
        imgW,
        imgH,
      );

      signBottom = sectionY + 4 + imgH;
    } catch {}
  } else {
    doc.line(rightX - 30, sectionY + 5, rightX, sectionY + 5);

    signBottom = sectionY + 5;
  }

  doc.setFont("Inter-Regular", "normal");
  doc.text("Authorized Signatory", rightX, signBottom + 3, { align: "right" });

  signBottom += 5;

  let sectionEndY = Math.max(bankY, sectionY + qrHeight, signBottom);
  
  y = currentSectionY +  4;

  if (storeSettings?.negobillBranding) {
    const minGapY = y + 3;
    const bottomLimitY = HALF_PAGE_HEIGHT - 5;
    let finalBrandingY = Math.max(minGapY, bottomLimitY);

    // Safety check if branding calculations push out of the active breakdown sheet
    if (finalBrandingY > doc.internal.pageSize.getHeight() - 10) {
      finalBrandingY = doc.internal.pageSize.getHeight() - 10;
    }

    const normalText = "Powered by ";
    const boldText = "NegoBill.com";

    doc.setFontSize(8);
    doc.setFont("Inter-Regular", "normal");
    const normalW = doc.getTextWidth(normalText);
    doc.setFont("Inter-Bold", "bold");
    const boldW = doc.getTextWidth(boldText);
    const totalWidth = normalW + boldW;
    const startX = MARGIN_RIGHT - totalWidth;

    doc.setFont("Inter-Regular", "normal");
    doc.text(normalText, startX, finalBrandingY);
    doc.setFont("Inter-Bold", "bold");
    doc.text(boldText, startX + normalW, finalBrandingY);
  }

  return doc;
};

const PremiumInvoice = ({
  data,
  qrCodeDataUrl,
  selectedTheme,  
  invoiceConfig,
}) => {
  const { storeSettings } = usePermission();

  const {
    documentConfig,

    showTaxDetails,
    showBillDetails,
  } = invoiceConfig;

  return (
    <>
      <>
        {showTaxDetails ? (
          <div className="tm_container">
            <div className="tm_invoice_wrap">
              <div className="tm_invoice tm_style1 tm_type1">
                <div className="tm_invoice_in" style={{ padding: "20px" }}>
                  <div className="tm_invoice_in p-2">
                    <table width="100%" className="pdfcontent">
                      <tbody>
                        <tr>
                          <td width="60%" style={{ textAlign: "left" }}>
                            <div className="d-flex gap-3 align-items-start">
                              <div className="tm_logo">
                                {documentConfig?.image && (
                                  <img
                                    src={documentConfig?.image}
                                    className="img-rounded logo mb-0"
                                    height="30"
                                  />
                                )}
                              </div>
                              <div
                                style={{ fontSize: "13px", lineHeight: "16px" }}
                              >
                                <div style={{ fontWeight: "600" }}>
                                  {documentConfig?.fromData?.title || ""}{" "}
                                </div>
                                <div className="font-12">
                                  {documentConfig?.fromData?.addressline1}{" "}
                                  {documentConfig?.fromData?.addressline2}{" "}
                                  {documentConfig?.fromData?.pincode || ""}
                                </div>

                                <div className="font-12">
                                  <span className="fw-bold">Ph:</span>{" "}
                                  {documentConfig?.fromData?.phone || ""}
                                  {documentConfig?.fromData
                                    ?.additionalPhone && (
                                    <>
                                      ,{" "}
                                      {documentConfig.fromData.additionalPhone}
                                    </>
                                  )}{" "}
                                </div>
                                {documentConfig?.fromData?.gstnumber && (
                                  <div>
                                    <span className="fw-bold">GSTIN:</span>{" "}
                                    {documentConfig?.fromData?.gstnumber}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td width="40%" style={{ textAlign: "right" }}>
                            <div className="tm_primary_color tm_f50 tm_text_uppercase font-14 fw-bold">
                              {documentConfig.title && (
                                <>{documentConfig.title}</>
                              )}
                            </div>
                            {data?.status === "CANCELLED" && (
                              <span className="badge rounded-pill bg-label-danger mt-3 font-14 tm_text_uppercase">
                                {data?.status}
                              </span>
                            )}
                            <div style={{ flex: "1", textAlign: "right" }}>
                              <div
                                className="mt-1"
                                style={{
                                  display: "inline-block",
                                  textAlign: "right",
                                }}
                              >
                                <div
                                  className=" font-12"
                                  style={{
                                    borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                    marginBottom: "5px",
                                  }}
                                >
                                  {documentConfig?.numberLabel}
                                  <span className="fw-bold">
                                    {" "}
                                    {documentConfig?.number}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-end font-12">
                                  <span>
                                    {" "}
                                    DATE: {documentConfig?.Date?.date}
                                  </span>
                                  <span className="ms-3">
                                    TIME:{documentConfig?.Date?.time}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {documentConfig?.toData && (
                    <div
                      className="d-flex mt-1"
                      style={{
                        border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                      }}
                    >
                      <div
                        style={{
                          flex: "1",
                          padding: "5px",
                          borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                        }}
                      >
                        <span className="fw-bold font-13">
                          To :{" "}
                          <span className="font-14 ">
                            {documentConfig?.toData?.name}
                          </span>
                        </span>

                        <div className="font-10">
                          {" "}
                          {documentConfig?.toData?.addressline1 &&
                            `${documentConfig?.toData?.addressline1}, `}
                          {documentConfig?.toData?.addressline2 &&
                            `${documentConfig?.toData?.addressline2}, `}
                          {documentConfig?.toData?.district &&
                            `${documentConfig?.toData?.district}, `}
                          {documentConfig?.toData?.pincode &&
                            ` - ${documentConfig?.toData?.pincode}`}
                        </div>
                        <div className="font-10">
                          {[
                            documentConfig?.toData?.gstnumber &&
                              `GSTIN: ${documentConfig?.toData.gstnumber}`,
                            documentConfig?.toData?.phone &&
                              `Mobile: ${documentConfig?.toData.phone}`,
                            documentConfig?.toData?.referenceid &&
                              `Reference No: ${documentConfig?.toData.referenceid}`,
                            documentConfig?.toData?.fssai &&
                              `FSSAI: ${documentConfig?.toData.fssai}`,
                            documentConfig?.toData?.cinnumber &&
                              `CIN: ${documentConfig?.toData.cinnumber}`,
                            documentConfig?.toData?.pannumber &&
                              `PAN: ${documentConfig?.toData.pannumber}`,
                          ]
                            .filter(Boolean)
                            .map((item, index, arr) => (
                              <React.Fragment key={index}>
                                <span>{item}</span>
                                {index < arr.length - 1 && (
                                  <span style={{ margin: "0 5px" }}>|</span>
                                )}
                              </React.Fragment>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <table
                    // className="items"
                    width="100%"
                    style={{
                      borderCollapse: "collapse",
                    }}
                    cellPadding="4"
                  >
                    <thead className="font-11">
                      <tr>
                        <th
                          className="force-style"
                          style={{
                            width: "20px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          S.No
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "240px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Description
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "120px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          HSN/SAC
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "50px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          MRP
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Base Price
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Qty
                        </th>

                        <th
                          className="force-style"
                          style={{
                            width: "30px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Tax Val
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Tax
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Tax Amt
                        </th>
                        <th
                          className="force-style"
                          style={{
                            width: "100px",
                            textAlign: "center",
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                          }}
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentConfig?.items?.map((product, index) => (
                        <tr key={index} className="font-11">
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "11px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "left",
                              fontSize: "11px",
                              width: "30%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            <div>{product?.title}</div>
                            {/* {product?.imode === "SERVICE" && (
                                      <div className="ms-0 fsz-10 lh-13 mb-1">
                                        {product?.description}
                                      </div>
                                    )} */}
                            {product.imode === "PRODUCT" && product.note && (
                              <div className="ms-0 fsz-10 mb-1">
                                {product?.note}
                              </div>
                            )}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "11px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.hsnsac}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "11px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.mrp}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "11px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.baseprice}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "11px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.quantity}
                            <span className="font-11 ms-1">
                              {(() => {
                                const unitCode = product?.isSecondaryUnit
                                  ? product?.unitConfig?.secondaryUnitCode
                                  : product?.unitConfig?.primaryUnitCode;

                                // return unitCode !== "unit" ? unitCode : "";
                                return unitCode || "";
                              })()}
                            </span>
                          </td>

                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "11px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.lineTotalBasePrice}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "11px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {`${product?.taxpercentage}%`}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "11px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.lineTotalTaxAmount}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "11px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product?.lineGrandTotal}
                          </td>
                        </tr>
                      ))}                    
                    </tbody>
                    <tfoot>
                      <tr className="font-11">
                        <td
                          colSpan="7"
                          style={{
                            border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "left",
                            padding: "6px",
                          }}
                        >
                          <span className="fw-bold">Rupees in words:</span>{" "}
                          {documentConfig?.amountInWords}
                        </td>
                        <td
                          className="font-11"
                          colSpan="3"
                          style={{
                            border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            padding: "6px",
                            fontWeight: "bold",
                          }}
                        >
                          TOTAL AMOUNT :{" "}
                          <span className="ms-2 font-11">
                            {" "}
                            {documentConfig?.grandTotal}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  {documentConfig?.remarks ? (
                    <>
                      <div className="font-12 mt-2 fw-600 text-black mb-1">
                        Remark
                      </div>

                      <div
                        className="font-12 mb-2 p-0"
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {capitalizeFirstLetter(documentConfig?.remarks)}
                      </div>
                    </>
                  ) : null}
                  <div className="row mt-2 p-2">
                    <div className="col-4 p-2 pt-0  border-dark font-11">
                      {documentConfig?.bankDetails && (
                          <>
                            <strong>Bank Details:</strong>
                            {documentConfig?.bankDetails?.accountNo && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>
                                  Account #:
                                </span>
                                {documentConfig?.bankDetails?.accountNo}
                              </div>
                            )}
                            {documentConfig?.bankDetails?.ifsc && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>IFSC:</span>
                                {documentConfig?.bankDetails?.ifsc}
                              </div>
                            )}
                            {documentConfig?.bankDetails?.beneficiaryName && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>Name:</span>
                                {documentConfig?.bankDetails?.beneficiaryName}
                              </div>
                            )}
                            {documentConfig?.bankDetails?.bankName && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>
                                  Bank Name:
                                </span>
                                {documentConfig?.bankDetails?.bankName}
                              </div>
                            )}
                            {documentConfig?.bankDetails?.branch && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>
                                  Branch Name:
                                </span>
                                {documentConfig?.bankDetails?.branch}
                              </div>
                            )}
                            {documentConfig?.upiId && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>UPI ID:</span>
                                {documentConfig?.upiId}
                              </div>
                            )}{" "}
                          </>
                        )}
                    </div>
                    {qrCodeDataUrl && (
                      <div className="col-3 d-flex align-items-center justify-content-end">
                        <img
                          src={qrCodeDataUrl}
                          alt="QR"
                          style={{ width: "100px", height: "100px" }}
                        />
                      </div>
                    )}

                    <div className="col-5  ms-auto">
                      <div className="text-end p-0 fw-bold font-12 mt-2 ">
                        {documentConfig?.fromData?.title}
                      </div>
                      <div>
                        {storeSettings?.invoiceSettings?.signature ? (
                          <div className="text-end p-0 fw-bold pb-1">
                            <img
                              src={
                                uRLs.IMAGE_URL +
                                storeSettings?.invoiceSettings?.signature
                              }
                              width={100}
                            />
                          </div>
                        ) : (
                          <div className="p-0 pt-4">
                            <hr
                              className="m-1 ms-auto "
                              style={{
                                border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                maxWidth: "120px",
                              }}
                            />
                          </div>
                        )}
                        <div className="text-end p-0 font-12">
                          Authorized Signatory
                        </div>
                      </div>
                    </div>
                  </div>

                  {storeSettings?.negobillBranding && (
                    <>
                      <div className="mt-5 font-12 d-flex justify-content-end ">
                        <span>
                          Powered By{" "}
                          <span className="fw-bold">NegoBill.com</span>
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : showBillDetails ? (
          <div className="tm_container">
            <div className="tm_invoice_wrap">
              <div className="tm_invoice tm_style1 tm_type1">
                <div className="tm_invoice_in" style={{ padding: "20px" }}>
                  <div className="tm_invoice_in p-2">
                    <table width="100%" className="pdfcontent">
                      <tbody>
                        <tr>
                          <td width="60%" style={{ textAlign: "left" }}>
                            <div className="d-flex gap-3 align-items-start">
                              <div className="tm_logo">
                                {documentConfig?.image && (
                                  <img
                                    src={documentConfig?.image}
                                    className="img-rounded logo mb-0"
                                    height="30"
                                  />
                                )}
                              </div>
                              <div
                                style={{ fontSize: "13px", lineHeight: "16px" }}
                              >
                                <div style={{ fontWeight: "600" }}>
                                  {documentConfig?.fromData?.title || ""}{" "}
                                </div>
                                <div className="font-12">
                                  {documentConfig?.fromData?.addressline1}{" "}
                                  {documentConfig?.fromData?.addressline2}{" "}
                                  {documentConfig?.fromData?.pincode || ""}
                                </div>

                                <div className="font-12">
                                  <span className="fw-bold">Ph:</span>{" "}
                                  {documentConfig?.fromData?.phone || ""}
                                  {documentConfig?.fromData
                                    ?.additionalPhone && (
                                    <>
                                      ,{" "}
                                      {documentConfig.fromData.additionalPhone}
                                    </>
                                  )}{" "}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td width="40%" style={{ textAlign: "right" }}>
                            <div className="tm_primary_color tm_f50 tm_text_uppercase font-14 fw-bold">
                              {documentConfig.title && (
                                <>{documentConfig.title}</>
                              )}
                            </div>
                            {data?.status === "CANCELLED" && (
                              <span className="badge rounded-pill bg-label-danger mt-3 font-14 tm_text_uppercase">
                                {data?.status}
                              </span>
                            )}
                            <div style={{ flex: "1", textAlign: "right" }}>
                              <div
                                className="mt-1"
                                style={{
                                  display: "inline-block",
                                  textAlign: "right",
                                }}
                              >
                                <div
                                  className=" font-12"
                                  style={{
                                    borderBottom: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                    marginBottom: "5px",
                                  }}
                                >
                                  {documentConfig?.numberLabel}
                                  <span className="fw-bold">
                                    {" "}
                                    {documentConfig?.number}
                                  </span>
                                </div>
                                <div className="d-flex justify-content-end font-12">
                                  <span>
                                    {" "}
                                    DATE: {documentConfig?.Date?.date}
                                  </span>
                                  <span className="ms-3">
                                    TIME:{documentConfig?.Date?.time}
                                  </span>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {documentConfig?.toData  && (
                    <div
                      className="d-flex mt-1"
                      style={{
                        border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                      }}
                    >
                      <div
                        style={{
                          flex: "1",
                          padding: "5px",
                          borderRight: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                        }}
                      >
                        <span className="fw-bold font-13">
                          To :{" "}
                          <span className="font-14 ">
                            {documentConfig?.toData?.name}
                          </span>
                        </span>

                        <div className="font-10">
                          {" "}
                          {documentConfig?.toData?.addressline1 &&
                            `${documentConfig?.toData?.addressline1}, `}
                          {documentConfig?.toData?.addressline2 &&
                            `${documentConfig?.toData?.addressline2}, `}
                          {documentConfig?.toData?.district &&
                            `${documentConfig?.toData?.district}, `}
                          {documentConfig?.toData?.pincode &&
                            ` - ${documentConfig?.toData?.pincode}`}
                        </div>
                        <div className="font-10">
                          {[
                            documentConfig?.toData?.gstnumber &&
                              `GSTIN: ${documentConfig?.toData.gstnumber}`,
                            documentConfig?.toData?.phone &&
                              `Mobile: ${documentConfig?.toData.phone}`,
                            documentConfig?.toData?.referenceid &&
                              `Reference No: ${documentConfig?.toData.referenceid}`,
                            documentConfig?.toData?.fssai &&
                              `FSSAI: ${documentConfig?.toData.fssai}`,
                            documentConfig?.toData?.cinnumber &&
                              `CIN: ${documentConfig?.toData.cinnumber}`,
                            documentConfig?.toData?.pannumber &&
                              `PAN: ${documentConfig?.toData.pannumber}`,
                          ]
                            .filter(Boolean)
                            .map((item, index, arr) => (
                              <React.Fragment key={index}>
                                <span>{item}</span>
                                {index < arr.length - 1 && (
                                  <span style={{ margin: "0 5px" }}>|</span>
                                )}
                              </React.Fragment>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}
                  <table
                    // className="items"
                    width="100%"
                    style={{
                      borderCollapse: "collapse",
                    }}
                    cellPadding="4"
                  >
                    <thead>
                      <tr>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          S.No
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          Description
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          HSN/SAC
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          MRP
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          Discount
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          Rate
                        </th>
                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          Qty
                        </th>

                        <th
                          className="force-style"
                          style={{
                            // width: "10px",
                            textAlign: "center",
                            //   background: "#f3f3f3",
                            "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            "--th-bg":
                              selectedTheme?.templateTheme?.primaryColor,
                            "--th-color":
                              selectedTheme?.templateTheme?.foregroundColor,
                          }}
                        >
                          Amount
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {documentConfig?.items?.map((product, index) => (
                        <tr key={index}>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              width: "5%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {index + 1}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              width: "65%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            <div>{product.title}</div>
                            {/* {product.imode === "SERVICE" && (
                                      <div className="ms-0 fsz-10 lh-13 mb-1">
                                        {product.description}
                                      </div>
                                    )} */}
                            {product.imode === "PRODUCT" && product.note && (
                              <div className="ms-0 fs-12 text-truncate-description text-muted">
                                {product?.note}
                              </div>
                            )}

                            {/* <div>
                                      {product.hsnsac && (
                                        <span>
                                          <span className="fw-medium">
                                            HSN/SAC :{" "}
                                          </span>{" "}
                                          {product.hsnsac}
                                        </span>
                                      )}
                                      {product.discount > 0 && (
                                        <>
                                          <>
                                            {" "}
                                            <span className="fw-medium">
                                              MRP :{" "}
                                            </span>{" "}
                                            
                                            {CurrencyConverter.toCurrency(
                                              product.mrp || 0,
                                              "INR"
                                            )}
                                          </>{" "}
                                          <>
                                            <span className="fw-medium">
                                              Discount :{" "}
                                            </span>{" "}
                                             
                                            {CurrencyConverter.toCurrency(
                                              product.discount || 0,
                                              "INR"
                                            )}
                                          </>
                                        </>
                                      )}{" "}
                                      {product.taxpercentage > 0 && (
                                        <>
                                          <span className="fw-medium">
                                            Tax :{" "}
                                          </span>{" "}
                                          
                                          {CurrencyConverter.toCurrency(
                                            product.baseprice || 0,
                                            "INR"
                                          )}
                                          @{product.taxpercentage}% = 
                                          {CurrencyConverter.toCurrency(
                                            product.taxamount || 0,
                                            "INR"
                                          )}
                                        </>
                                      )}
                                    </div> */}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.hsnsac}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.mrp}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.discount}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              width: "10%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.soldprice}
                          </td>
                          <td
                            className="force-style"
                            style={{
                              textAlign: "center",
                              fontSize: "12px",
                              width: "10%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.quantity}
                            <span className="font-11 ms-1">
                              {(() => {
                                const unitCode = product?.isSecondaryUnit
                                  ? product?.unitConfig?.secondaryUnitCode
                                  : product?.unitConfig?.primaryUnitCode;

                                // return unitCode !== "unit" ? unitCode : "";
                                return unitCode || "";
                              })()}
                            </span>
                          </td>

                          <td
                            className="force-style"
                            style={{
                              textAlign: "right",
                              fontSize: "12px",
                              width: "10%",
                              "--td-border": `0.5mm solid ${selectedTheme?.templateTheme?.borderColor} `,
                            }}
                          >
                            {product.lineGrandTotal}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="font-11">
                        <td
                          colSpan="5"
                          style={{
                            border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "left",
                            padding: "6px",
                          }}
                        >
                          <span className="fw-bold">Rupees in words:</span>{" "}
                          {documentConfig?.amountInWords}
                        </td>
                        <td
                          className="font-11"
                          colSpan="3"
                          style={{
                            border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                            textAlign: "right",
                            padding: "6px",
                            fontWeight: "bold",
                          }}
                        >
                          TOTAL AMOUNT :{" "}
                          <span className="ms-2 font-11">
                            {" "}
                            {documentConfig?.grandTotal}
                          </span>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                  {documentConfig?.remarks ? (
                    <>
                      <div className="font-12 mt-2 fw-600 text-black mb-1">
                        Remark
                      </div>

                      <div
                        className="font-12 mb-2 p-0"
                        style={{
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {capitalizeFirstLetter(documentConfig?.remarks)}
                      </div>
                    </>
                  ) : null}
                  <div className="row mt-2 p-2">
                    <div className="col-4 p-2 pt-0  border-dark font-11">
                      {documentConfig?.bankDetails && (
                          <>
                            <strong>Bank Details:</strong>
                            {documentConfig?.bankDetails?.accountNo && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>
                                  Account #:
                                </span>
                                {documentConfig?.bankDetails?.accountNo}
                              </div>
                            )}
                            {documentConfig?.bankDetails?.ifsc && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>IFSC:</span>
                                {documentConfig?.bankDetails?.ifsc}
                              </div>
                            )}
                            {documentConfig?.bankDetails?.beneficiaryName && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>Name:</span>
                                {documentConfig?.bankDetails?.beneficiaryName}
                              </div>
                            )}
                            {documentConfig?.bankDetails?.bankName && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>
                                  Bank Name:
                                </span>
                                {documentConfig?.bankDetails?.bankName}
                              </div>
                            )}
                            {documentConfig?.bankDetails?.branch && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>
                                  Branch Name:
                                </span>
                                {documentConfig?.bankDetails?.branch}
                              </div>
                            )}
                            {documentConfig?.upiId && (
                              <div className="d-flex font-11">
                                <span style={{ width: "100px" }}>UPI ID:</span>
                                {documentConfig?.upiId}
                              </div>
                            )}{" "}
                          </>
                        )}
                    </div>
                    {qrCodeDataUrl  && (
                      <div className="col-3 d-flex align-items-center justify-content-end">
                        <img
                          src={qrCodeDataUrl}
                          alt="QR"
                          style={{ width: "100px", height: "100px" }}
                        />
                      </div>
                    )}

                    <div className="col-5  ms-auto">
                      <div className="text-end p-0 fw-bold font-12 mt-2 ">
                        {documentConfig?.fromData?.title}
                      </div>
                      <div>
                        {storeSettings?.invoiceSettings?.signature ? (
                          <div className="text-end p-0 fw-bold pb-1">
                            <img
                              src={
                                uRLs.IMAGE_URL +
                                storeSettings?.invoiceSettings?.signature
                              }
                              width={100}
                            />
                          </div>
                        ) : (
                          <div className="p-0 pt-4">
                            <hr
                              className="m-1 ms-auto "
                              style={{
                                border: `1px solid ${selectedTheme?.templateTheme?.borderColor}`,
                                maxWidth: "120px",
                              }}
                            />
                          </div>
                        )}
                        <div className="text-end p-0 font-12">
                          Authorized Signatory
                        </div>
                      </div>
                    </div>
                  </div>

                  {storeSettings?.negobillBranding && (
                    <>
                      <div className="mt-5 font-12 d-flex justify-content-end ">
                        <span>
                          Powered By{" "}
                          <span className="fw-bold">NegoBill.com</span>
                        </span>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="default-content">
            <p>No content available for this sale mode.</p>
          </div>
        )}
      </>
    </>
  );
};

export default PremiumInvoice;
